import { ConfigProvider, Modal, message } from "antd";
import esES from "antd/es/locale/es_ES";
import { useCallback, useMemo, useRef, useState } from "react";
import { GlobalContext } from "./components/context/GlobalContext";
import { AppRouter } from "./components/router/AppRouter";
import { useAlumnos } from "./components/hooks/useAlumnos";
import { useInstructores } from "./components/hooks/useInstructores";
import { useCursos } from "./components/hooks/useCursos";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  HttpLink,
  from,
  split,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { useConfig } from "./components/hooks/useConfig";
import { useHeadquarters } from "./components/hooks/useHeadquarters";
import dayjs from "dayjs";
// import { GET_VERSION } from "./graphql/query/Version";
import { SessionProvider } from "./components/session/SessionProvider";
import { useSession } from "./components/hooks/useSession";
import {
  getSelectedHeadquarterId,
  getToken,
  notifySessionInvalid,
} from "./utils/session";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import "./styles/index";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);


const httpLink = new HttpLink({
  uri: process.env.REACT_APP_HTTP_URL,
});

/**
 * The two values that identify a request: the session token and, for staff,
 * the sede being worked in. Read at send time, so a login or a sede switch
 * applies to the very next request without rebuilding the client.
 */
const sessionHeaders = ({ withoutHeadquarter = false } = {}) => {
  const headers = {};

  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;

  const headquarterId = getSelectedHeadquarterId();
  if (headquarterId && !withoutHeadquarter) {
    headers["x-headquarter-id"] = headquarterId;
  }

  return headers;
};

// Browsers cannot set headers on a websocket, so the same values travel as
// connectionParams. A function, so every (re)connection reads them fresh.
const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_WS_URL,
    connectionParams: () => sessionHeaders(),
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink,
);

const authMiddleware = new ApolloLink((operation, forward) => {
  const { headers = {}, withoutHeadquarter } = operation.getContext();

  operation.setContext({
    headers: { ...headers, ...sessionHeaders({ withoutHeadquarter }) },
  });
  return forward(operation);
});

// The API answers UNAUTHENTICATED when a token it was sent is expired,
// revoked by a password change, or belongs to a disabled account. That ends
// the session everywhere at once, instead of each screen failing on its own.
// Only when a token was actually sent: an anonymous call to a guarded
// operation is not a session ending.
const sessionErrorLink = onError(({ graphQLErrors, operation }) => {
  const unauthenticated = graphQLErrors?.find(
    (error) => error.extensions?.code === "UNAUTHENTICATED",
  );
  if (!unauthenticated) return;

  const sentToken = operation.getContext().headers?.authorization;
  if (!sentToken) return;

  notifySessionInvalid(
    unauthenticated.extensions?.reason,
    unauthenticated.message,
  );
});

const myClient = new ApolloClient({
  link: from([sessionErrorLink, authMiddleware, splitLink]),
  cache: new InMemoryCache(),
});

const App = () => {
  const session = useSession();

  // The staff user as the app has always read it off GlobalContext — the
  // session's user plus the sede being worked in, which several search hooks
  // watch to reset when it changes. Null for students and when signed out, so
  // the admin data hooks below never fire for them.
  const user = useMemo(
    () =>
      session.user
        ? {
            ...session.user,
            selectedHeadquarterId: session.selectedHeadquarterId,
          }
        : null,
    [session.user, session.selectedHeadquarterId],
  );

  const [alumnos, setAlumnos] = useState(null);
  const [instructores, setInstructores] = useState(null);
  const [cursos, setCursos] = useState(null);
  const [config, setConfig] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [drawerContent, setDrawerContent] = useState({
    visible: false,
    type: "",
    item: {},
  });
  const [modalApi, modalContextHolder] = Modal.useModal();

  // A form in the drawer can set this to a function that says whether it holds
  // unsaved input (see CreateCourseTurn). Every way of closing the drawer — the
  // mask, Escape, a Cancelar button — goes through closeDrawer, which asks
  // before throwing that input away. Closing after a save uses
  // setDrawerContent directly: nothing is lost then.
  const drawerCloseGuard = useRef(null);
  const closeDrawer = useCallback(() => {
    const close = () => setDrawerContent({ visible: false, type: "", item: {} });
    if (!drawerCloseGuard.current?.()) {
      close();
      return;
    }
    modalApi.confirm({
      title: "¿Descartar los cambios?",
      content: "Lo que cargaste en este formulario se va a perder.",
      okText: "Descartar",
      okButtonProps: { danger: true },
      cancelText: "Seguir editando",
      onOk: close,
    });
  }, [modalApi]);

  const [loadingGlobal, setLoadingGlobal] = useState({
    alumnos: false,
    instructores: false,
    cursos: false,
    config: false,
  });

  const [cursosPoll, setCursosPoll] = useState({});
  const [configPoll, setConfigPoll] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [moves, setMoves] = useState([]);
  const [movesPoll, setMovesPoll] = useState({});
  const [filteredMoves, setFilteredMoves] = useState([]);

  // const {
  //   data: dataVersion,
  //   loading: loadingVersion,
  //   error: errorVersion,
  // } = useQuery(GET_VERSION);

  // Condicionales
  useAlumnos(setAlumnos, messageApi, setLoadingGlobal, user);
  useInstructores(setInstructores, messageApi, setLoadingGlobal, user);
  useCursos(setCursos, messageApi, setLoadingGlobal, setCursosPoll, user);
  useConfig(setConfig, messageApi, setLoadingGlobal, setConfigPoll, user);
  useHeadquarters(setHeadquarters, messageApi, user);

  document.addEventListener("wheel", function () {
    if (document.activeElement.type === "number") {
      document.activeElement.blur();
    }
  });

  return (
    <>
      <GlobalContext.Provider
        value={{
          // Read-only here: the session owns the user. Change it through
          // useSession() — login, logout, refresh, selectHeadquarter.
          user,
          alumnos,
          setAlumnos,
          instructores,
          setInstructores,
          cursos,
          setCursos,
          drawerContent,
          setDrawerContent,
          closeDrawer,
          drawerCloseGuard,
          loadingGlobal,
          setLoadingGlobal,
          messageApi,
          contextHolder,
          cursosPoll,
          setCursosPoll,
          config,
          setConfig,
          configPoll,
          setConfigPoll,
          profiles,
          setProfiles,
          users,
          setUsers,
          headquarters,
          setHeadquarters,
          moves,
          setMoves,
          setMovesPoll,
          movesPoll,
          filteredMoves,
          setFilteredMoves,
        }}
      >
        <div className="App">
          {contextHolder}
          {modalContextHolder}
          {process.env.REACT_APP_HTTP_URL?.startsWith("http://localhost") && (
            <span className="span-testing">TESTING...</span>
          )}
          <AppRouter />
        </div>
      </GlobalContext.Provider>
    </>
  );
};

const Main = () => {
  const customTheme = {
    token: {
      colorPrimary: "#2DACBD",
    },
    components: {
      Select: {
        colorPrimary: "#2DACBD",
      },
    },
  };

  return (
    <ApolloProvider client={myClient}>
      <ConfigProvider locale={esES} theme={customTheme}>
        {/* Inside Apollo, which it queries; around App, which reads it. */}
        <SessionProvider>
          <App />
        </SessionProvider>
      </ConfigProvider>
    </ApolloProvider>
  );
};

export default Main;
